import { ChevronLeftIcon } from '@heroicons/react/16/solid'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { DescriptionDetails, DescriptionList, DescriptionTerm } from '../catalyst/description-list'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../catalyst/dialog'
import { Heading, Subheading } from '../catalyst/heading'
import { Link } from '../catalyst/link'
import { Text } from '../catalyst/text'
import { Textarea } from '../catalyst/textarea'
import { formatArea, orgAreaUnit } from '../lib/area'
import { useAuth } from '../lib/auth'
import { orgYieldUnit, yieldInputValue, yieldUnitLabel } from '../lib/yield'
import { useFarms } from '../lib/farms'
import { useField } from '../lib/field'
import { canadaGapBadgeProps, useCanadaGapStatus } from '../lib/canadagap'
import { ComplianceBadge } from '../lib/compliance-badge'
import type { Field } from '../lib/types'
import { FieldFormDialog } from './field-form-dialog'
import { LogButton } from '../wizard/log-button'
import { Shell } from './shell'

/** DRIP -> Drip, RIVER_CREEK -> River creek. */
const titleCase = (value: string) => value.charAt(0) + value.slice(1).toLowerCase().replace(/_/g, ' ')
const formatDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

function ArchiveFieldDialog({ open, onClose, field, onConfirm }: { open: boolean; onClose: () => void; field: Field; onConfirm: () => Promise<void> }) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleConfirm() {
    setSubmitting(true)
    setError(null)
    try {
      await onConfirm()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not archive field.')
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Archive {field.name}?</DialogTitle>
      <DialogDescription>It will no longer appear on the farm. Its notes and history are kept.</DialogDescription>
      {error && (
        <DialogBody>
          <Text className="text-red-600 dark:text-red-400">{error}</Text>
        </DialogBody>
      )}
      <DialogActions>
        <Button plain onClick={onClose}>
          Cancel
        </Button>
        <Button color="red" onClick={handleConfirm} disabled={submitting}>
          {submitting ? 'Archiving…' : 'Archive'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function Notes({ notes, addNote }: { notes: ReturnType<typeof useField>['notes']; addNote: (body: string) => Promise<void> }) {
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!body.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await addNote(body.trim())
      setBody('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add note.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="mt-10">
      <Subheading>Notes</Subheading>
      <form onSubmit={handleSubmit} className="mt-4 max-w-xl">
        <Textarea value={body} onChange={(event) => setBody(event.target.value)} placeholder="Add a note about this field…" rows={3} />
        {error && <Text className="mt-2 text-red-600 dark:text-red-400">{error}</Text>}
        <div className="mt-3 flex justify-end">
          <Button type="submit" disabled={submitting || !body.trim()}>
            {submitting ? 'Adding…' : 'Add note'}
          </Button>
        </div>
      </form>
      {notes.length === 0 ? (
        <Text className="mt-4">No notes yet.</Text>
      ) : (
        <ul className="mt-4 max-w-xl space-y-3">
          {[...notes].reverse().map((note) => (
            <li key={note.id} className="rounded-lg p-4 ring-1 ring-zinc-950/5 dark:ring-white/10">
              <p className="text-sm/6 whitespace-pre-wrap text-zinc-950 dark:text-white">{note.body}</p>
              <Text className="mt-1 text-xs/5!">{formatDate(note.createdAt)}</Text>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export function FieldPage() {
  const { farmId, fieldId } = useParams<{ farmId: string; fieldId: string }>()
  const navigate = useNavigate()
  const { org } = useAuth()
  const areaUnit = orgAreaUnit(org)
  const yieldUnit = orgYieldUnit(org)
  const canWriteFields = org?.role !== 'AGRONOMIST'
  const { getFarm } = useFarms()
  const { field, notes, loading, error, updateField, addNote, archiveField } = useField(org?.id, farmId, fieldId)
  const { status: canadaGap } = useCanadaGapStatus(org?.id, farmId, fieldId, new Date().getFullYear())
  const [editOpen, setEditOpen] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)

  const farm = farmId ? getFarm(farmId) : undefined
  const backHref = `/farms/${farmId}`

  return (
    <Shell>
      <Link href={backHref} className="inline-flex items-center gap-1 text-sm/6 font-medium text-zinc-500 dark:text-zinc-400">
        <ChevronLeftIcon className="size-4" />
        {farm?.name ?? 'Farm'}
      </Link>

      {error ? (
        <div className="mt-6 rounded-lg border border-dashed border-red-300 p-10 text-center dark:border-red-900">
          <Text>{error}</Text>
        </div>
      ) : loading || !field ? (
        <div className="mt-6 rounded-lg border border-dashed border-zinc-950/15 p-10 text-center dark:border-white/15">
          <Text>Loading field…</Text>
        </div>
      ) : (
        <>
          <div className="mt-4 flex items-center justify-between gap-4">
            <div>
              <Heading>{field.name}</Heading>
              <Text>{formatArea(field.areaM2, areaUnit)}</Text>
            </div>
            {canWriteFields && !field.archivedAt && (
              <div className="flex items-center gap-3">
                <LogButton farmId={farmId!} fieldId={fieldId} />
                <Button outline onClick={() => setEditOpen(true)}>
                  Edit
                </Button>
                <Button color="red" onClick={() => setArchiveOpen(true)}>
                  Archive
                </Button>
              </div>
            )}
          </div>

          <DescriptionList className="mt-8 max-w-xl">
            <DescriptionTerm>Farm</DescriptionTerm>
            <DescriptionDetails>{farm?.name ?? '—'}</DescriptionDetails>
            <DescriptionTerm>Area</DescriptionTerm>
            <DescriptionDetails>
              {formatArea(field.areaM2, areaUnit)}
            </DescriptionDetails>
            <DescriptionTerm>Variety</DescriptionTerm>
            <DescriptionDetails>{[...new Set(field.plantings.map((p) => p.variety).filter(Boolean))].join(', ') || '—'}</DescriptionDetails>
            <DescriptionTerm>Year planted</DescriptionTerm>
            <DescriptionDetails>{[...new Set(field.plantings.map((p) => p.yearPlanted).filter(Boolean))].join(', ') || '—'}</DescriptionDetails>
            <DescriptionTerm>Irrigation</DescriptionTerm>
            <DescriptionDetails>{field.irrigationMethods.map(titleCase).join(', ') || '—'}</DescriptionDetails>
            <DescriptionTerm>Water source</DescriptionTerm>
            <DescriptionDetails>{field.waterSources.map(titleCase).join(', ') || '—'}</DescriptionDetails>
            <DescriptionTerm>Production</DescriptionTerm>
            <DescriptionDetails>{field.productionType ? titleCase(field.productionType) : '—'}</DescriptionDetails>
            <DescriptionTerm>Previous crop</DescriptionTerm>
            <DescriptionDetails>{field.previousCrop || '—'}</DescriptionDetails>
            <DescriptionTerm>Expected yield</DescriptionTerm>
            <DescriptionDetails>
              {field.expectedYieldKgPerHa == null
                ? '—'
                : `${yieldInputValue(field.expectedYieldKgPerHa, yieldUnit)} ${yieldUnitLabel(yieldUnit)} for ${field.expectedYieldYear}`}
            </DescriptionDetails>
            <DescriptionTerm>Description</DescriptionTerm>
            <DescriptionDetails>{field.notes || '—'}</DescriptionDetails>
            <DescriptionTerm>Added</DescriptionTerm>
            <DescriptionDetails>{formatDate(field.createdAt)}</DescriptionDetails>
            <DescriptionTerm>Last updated</DescriptionTerm>
            <DescriptionDetails>{formatDate(field.updatedAt)}</DescriptionDetails>
          </DescriptionList>

          <div className="mt-8 flex items-center justify-between gap-4">
            <div>
              <Subheading>Spray logs</Subheading>
              <div className="flex flex-wrap gap-1.5">
                <ComplianceBadge framework="CanadaGAP" formCode="H1" formName="Agronomic Inputs (Agricultural Chemicals)" {...canadaGapBadgeProps(canadaGap, 'h1')} />
              </div>
            </div>
            <Link href={`/farms/${farmId}/fields/${fieldId}/spray-logs`} className="shrink-0">View →</Link>
          </div>

          <div className="mt-4 flex items-center justify-between gap-4">
            <div>
              <Subheading>Fertilizer logs</Subheading>
              <div className="flex flex-wrap gap-1.5">
                <ComplianceBadge framework="CanadaGAP" formCode="H2" formName="Agronomic Inputs (Other)" section="Section 1 — Fertilizer" {...canadaGapBadgeProps(canadaGap, 'h2Fertilizer')} />
              </div>
            </div>
            <Link href={`/farms/${farmId}/fields/${fieldId}/fertilizer-logs`} className="shrink-0">View →</Link>
          </div>

          <div className="mt-4 flex items-center justify-between gap-4">
            <div>
              <Subheading>Amendment logs</Subheading>
              <div className="flex flex-wrap gap-1.5">
                <ComplianceBadge framework="CanadaGAP" formCode="H2" formName="Agronomic Inputs (Other)" section="Section 2 — Amendments" {...canadaGapBadgeProps(canadaGap, 'h2Amendment')} />
              </div>
            </div>
            <Link href={`/farms/${farmId}/fields/${fieldId}/amendment-logs`} className="shrink-0">View →</Link>
          </div>

          <div className="mt-4 flex items-center justify-between gap-4">
            <div>
              <Subheading>Harvest logs</Subheading>
              <div className="flex flex-wrap gap-1.5">
                <ComplianceBadge framework="CanadaGAP" formCode="P2" formName="Harvesting and Storing Product" {...canadaGapBadgeProps(canadaGap, 'p2')} />
              </div>
            </div>
            <Link href={`/farms/${farmId}/fields/${fieldId}/harvest-logs`} className="shrink-0">View →</Link>
          </div>

          <div className="mt-4 flex items-center justify-between gap-4">
            <Subheading>Scouting</Subheading>
            <Link href={`/farms/${farmId}/fields/${fieldId}/scouting-observations`} className="shrink-0">View →</Link>
          </div>

          <div className="mt-4 flex items-center justify-between gap-4">
            <Subheading>Field work</Subheading>
            <Link href={`/farms/${farmId}/fields/${fieldId}/field-tasks`} className="shrink-0">View →</Link>
          </div>

          <Notes notes={notes} addNote={addNote} />

          {canWriteFields && !field.archivedAt && <FieldFormDialog key={field.updatedAt} open={editOpen} onClose={() => setEditOpen(false)} field={field} onSave={updateField} />}
          <ArchiveFieldDialog
            open={archiveOpen}
            onClose={() => setArchiveOpen(false)}
            field={field}
            onConfirm={async () => {
              await archiveField()
              navigate(backHref, { replace: true })
            }}
          />
        </>
      )}
    </Shell>
  )
}
